// ---------------------------------------------------------------------------
// studio-starters-copy.ts — every readable string on `/studio/templates`.
//
// THE HARD PART OF THIS SCREEN IS NOT THE BUTTONS, IT IS THE CATALOG.
// A template card renders three things that come from
// `packages/cogo-schema/template-catalog.json` rather than from any component:
// the DOMAIN chip, the TAG chips, and a one-line DESCRIPTION. The catalog is a
// shared package with consumers outside this app, so its English is data, not
// copy, and translating it in place is not this lane's to do.
//
// So the description is TRANSLATED BY PARSING THE CATALOG'S OWN CLAIM rather
// than by re-deriving one from `blocks`. `template-description-copy.ts` reads
//   "<page part> <domain> template with <N> blocks featuring <clause, clause>"
// and re-renders it from `describe` below. Two consequences, both deliberate:
//   - under `en` the catalog string is passed through VERBATIM, so the English
//     surface is byte-for-byte what it was before this lane;
//   - a localized card can never claim something the catalog did not, because
//     every word in it came from the catalog's own sentence.
// A description that does not parse falls back to the English string, and
// `__tests__/template-description-copy.test.ts` fails when any catalog entry
// stops parsing or introduces a domain, tag or clause term with no label.
//
// KEEP-LATIN, deliberate: `cogo` (brand) and the tier GRADES `A`/`B`, which
// are grades and not words — `Tier` itself translates.
// ---------------------------------------------------------------------------

/** One of the four artifact types the Starter-paths row offers. */
export interface StarterPathCardCopy {
  title: string;
  description: string;
  cta: string;
}

export interface StartersCopy {
  pageDescription: string;
  /** Both the section's `aria-label` and its visible heading — one string,
   *  because WCAG 2.5.3 wants the accessible name to BE the visible words. */
  starterPaths: string;
  starterPathsLead: string;
  paths: {
    app: StarterPathCardCopy;
    document: StarterPathCardCopy;
    report: StarterPathCardCopy;
    revision: StarterPathCardCopy;
  };
  gallery: {
    searchPlaceholder: string;
    findSimilarBlocks: string;
    all: string;
    tier: (tierGrade: string) => string;
    blockCatalog: string;
    communityTemplates: string;
    comingSoon: string;
    emptyTitle: string;
    emptyBody: string;
    preview: string;
    create: string;
    previewAria: (labelName: string) => string;
    createFromAria: (labelName: string) => string;
    pages: (howMany: number) => string;
    blocks: (howMany: number) => string;
  };
  failedToStart: string;
  /** The project name used when a Starter card is used but the catalog entry
   *  has no name of its own — it becomes the created project's title. */
  newAppFallbackName: string;
  catalog: {
    /** Keyed by the catalog's own `domain` value. */
    domains: Record<string, string>;
    /** Keyed by the catalog's own `tags` values. */
    tags: Record<string, string>;
    /** Keyed by the clause terms the catalog's descriptions use verbatim. */
    features: Record<string, string>;
    /**
     * Re-renders a parsed catalog description. `features` arrives already
     * joined in the locale's own list style.
     */
    describe: (parts: {
      pageCount: number;
      blockCount: number;
      domainLabel: string;
      featureList: string;
    }) => string;
    /** Separator used to join `features` for this locale. */
    featureSeparator: string;
  };
}

export const EN_STARTERS_COPY: StartersCopy = {
  pageDescription:
    "Start from a cogo-ready app, document, or report, then customize the result in chat before review and sharing.",
  starterPaths: "Starter paths",
  starterPathsLead:
    "Choose the artifact type first. cogo can then prepare the source, result, review loop, and share path around it.",
  paths: {
    app: {
      title: "App",
      description:
        "Interactive workflows, screens, and publishable product experiences.",
      cta: "Start app",
    },
    document: {
      title: "Document",
      description:
        "Policies, guides, proposals, decks, or source files shaped into living documents.",
      cta: "Add document source",
    },
    report: {
      title: "Report",
      description:
        "Source-backed analysis with tables, charts, summaries, and review readiness.",
      cta: "Add report data",
    },
    revision: {
      title: "Revision",
      description:
        "Open saved work, ask for a change, and review the proposed result before sharing.",
      cta: "Open saved work",
    },
  },
  gallery: {
    searchPlaceholder: "Search templates...",
    findSimilarBlocks: "Find similar blocks",
    all: "All",
    tier: (tierGrade) => `Tier ${tierGrade}`,
    blockCatalog: "Block Catalog",
    communityTemplates: "Community Templates",
    comingSoon: "Coming soon",
    emptyTitle: "No templates found",
    emptyBody:
      "No templates match your search or filters. Try clearing a filter or broadening your search.",
    preview: "Preview",
    create: "Create",
    previewAria: (labelName) => `Preview ${labelName}`,
    createFromAria: (labelName) => `Create app from ${labelName}`,
    pages: (howMany) => `${howMany} pages`,
    blocks: (howMany) => `${howMany} blocks`,
  },
  failedToStart: "Failed to start from Starters",
  newAppFallbackName: "New App",
  catalog: {
    // The catalog's own vocabulary, verbatim — the English surface renders
    // these raw values today and must keep rendering exactly them.
    domains: {
      admin: "admin",
      agency: "agency",
      analytics: "analytics",
      content: "content",
      crm: "crm",
      ecommerce: "ecommerce",
      food: "food",
      health: "health",
      portfolio: "portfolio",
      productivity: "productivity",
      saas: "saas",
      support: "support",
    },
    tags: {
      admin: "admin",
      agency: "agency",
      analytics: "analytics",
      cards: "cards",
      content: "content",
      crm: "crm",
      data: "data",
      ecommerce: "ecommerce",
      food: "food",
      footer: "footer",
      forms: "forms",
      health: "health",
      hero: "hero",
      navigation: "navigation",
      portfolio: "portfolio",
      pro: "pro",
      productivity: "productivity",
      saas: "saas",
      support: "support",
    },
    features: {
      navigation: "navigation",
      "hero section": "hero section",
      charts: "charts",
      "data tables": "data tables",
      forms: "forms",
      footer: "footer",
    },
    describe: ({ pageCount, blockCount, domainLabel, featureList }) =>
      `${pageCount === 1 ? "Single-page" : `${pageCount}-page`} ${domainLabel} template with ${blockCount} blocks featuring ${featureList}`,
    featureSeparator: ", ",
  },
};

export const KO_STARTERS_COPY: StartersCopy = {
  pageDescription:
    "cogo가 바로 쓸 수 있는 앱, 문서, 리포트에서 시작한 다음, 검토하고 공유하기 전에 채팅에서 결과를 다듬으세요.",
  starterPaths: "시작 경로",
  starterPathsLead:
    "먼저 산출물 유형을 고르세요. 그러면 cogo가 그에 맞춰 소스, 결과, 검토 흐름, 공유 경로를 준비합니다.",
  paths: {
    app: {
      title: "앱",
      description: "동작하는 업무 흐름, 화면, 그리고 게시할 수 있는 제품 경험.",
      cta: "앱 시작",
    },
    document: {
      title: "문서",
      description:
        "정책, 안내서, 제안서, 발표 자료, 소스 파일을 살아 있는 문서로.",
      cta: "문서 소스 추가",
    },
    report: {
      title: "리포트",
      description: "표, 차트, 요약, 검토 준비까지 갖춘 소스 기반 분석.",
      cta: "리포트 데이터 추가",
    },
    revision: {
      title: "개정",
      description:
        "저장한 작업을 열어 변경을 요청하고, 공유하기 전에 제안된 결과를 검토하세요.",
      cta: "저장한 작업 열기",
    },
  },
  gallery: {
    searchPlaceholder: "템플릿 검색...",
    findSimilarBlocks: "비슷한 블록 찾기",
    all: "전체",
    tier: (tierGrade) => `${tierGrade}등급`,
    blockCatalog: "블록 카탈로그",
    communityTemplates: "커뮤니티 템플릿",
    comingSoon: "준비 중",
    emptyTitle: "템플릿을 찾지 못했습니다",
    emptyBody:
      "검색어나 필터와 맞는 템플릿이 없습니다. 필터를 해제하거나 검색 범위를 넓혀 보세요.",
    preview: "미리보기",
    create: "만들기",
    previewAria: (labelName) => `${labelName} 미리보기`,
    createFromAria: (labelName) => `${labelName}에서 앱 만들기`,
    pages: (howMany) => `${howMany}페이지`,
    blocks: (howMany) => `블록 ${howMany}개`,
  },
  failedToStart: "스타터에서 시작하지 못했습니다",
  newAppFallbackName: "새 앱",
  catalog: {
    domains: {
      admin: "관리",
      agency: "에이전시",
      analytics: "분석",
      content: "콘텐츠",
      crm: "고객 관리",
      ecommerce: "커머스",
      food: "음식",
      health: "건강",
      portfolio: "포트폴리오",
      productivity: "생산성",
      saas: "구독형 서비스",
      support: "고객 지원",
    },
    tags: {
      admin: "관리",
      agency: "에이전시",
      analytics: "분석",
      cards: "카드",
      content: "콘텐츠",
      crm: "고객 관리",
      data: "데이터",
      ecommerce: "커머스",
      food: "음식",
      footer: "바닥글",
      forms: "양식",
      health: "건강",
      hero: "대표 영역",
      navigation: "내비게이션",
      portfolio: "포트폴리오",
      pro: "프로",
      productivity: "생산성",
      saas: "구독형 서비스",
      support: "고객 지원",
    },
    features: {
      navigation: "내비게이션",
      "hero section": "대표 영역",
      charts: "차트",
      "data tables": "데이터 표",
      forms: "양식",
      footer: "바닥글",
    },
    describe: ({ pageCount, blockCount, domainLabel, featureList }) =>
      `블록 ${blockCount}개로 이루어진 ${pageCount}페이지 ${domainLabel} 템플릿 · ${featureList}`,
    featureSeparator: ", ",
  },
};

export const JA_STARTERS_COPY: StartersCopy = {
  pageDescription:
    "cogo がすぐ使えるアプリ、ドキュメント、レポートから始めて、確認して共有する前にチャットで結果を仕上げましょう。",
  starterPaths: "出発点の種類",
  starterPathsLead:
    "まず成果物の種類を選んでください。そのうえで cogo が、それに合わせた素材、結果、確認の流れ、共有の道筋を用意します。",
  paths: {
    app: {
      title: "アプリ",
      description: "動く業務フロー、画面、そして公開できる製品体験。",
      cta: "アプリを始める",
    },
    document: {
      title: "ドキュメント",
      description:
        "規程、手引き、提案書、スライド、素材ファイルを、生きたドキュメントへ。",
      cta: "ドキュメントの素材を追加",
    },
    report: {
      title: "レポート",
      description: "表、グラフ、要約、確認の備えまで揃った、素材にもとづく分析。",
      cta: "レポートのデータを追加",
    },
    revision: {
      title: "改訂",
      description:
        "保存した作業を開いて変更を依頼し、共有する前に提案された結果を確認します。",
      cta: "保存した作業を開く",
    },
  },
  gallery: {
    searchPlaceholder: "テンプレートを検索...",
    findSimilarBlocks: "似たブロックを探す",
    all: "すべて",
    tier: (tierGrade) => `${tierGrade} 等級`,
    blockCatalog: "ブロックカタログ",
    communityTemplates: "コミュニティのテンプレート",
    comingSoon: "近日公開",
    emptyTitle: "テンプレートが見つかりません",
    emptyBody:
      "検索語や絞り込みに合うテンプレートがありません。絞り込みを外すか、検索の幅を広げてみてください。",
    preview: "プレビュー",
    create: "作成",
    previewAria: (labelName) => `${labelName} をプレビュー`,
    createFromAria: (labelName) => `${labelName} からアプリを作成`,
    pages: (howMany) => `${howMany} ページ`,
    blocks: (howMany) => `ブロック ${howMany} 個`,
  },
  failedToStart: "スターターから開始できませんでした",
  newAppFallbackName: "新しいアプリ",
  catalog: {
    domains: {
      admin: "管理",
      agency: "エージェンシー",
      analytics: "分析",
      content: "コンテンツ",
      crm: "顧客管理",
      ecommerce: "コマース",
      food: "飲食",
      health: "健康",
      portfolio: "ポートフォリオ",
      productivity: "生産性",
      saas: "サブスク型サービス",
      support: "サポート",
    },
    tags: {
      admin: "管理",
      agency: "エージェンシー",
      analytics: "分析",
      cards: "カード",
      content: "コンテンツ",
      crm: "顧客管理",
      data: "データ",
      ecommerce: "コマース",
      food: "飲食",
      footer: "フッター",
      forms: "フォーム",
      health: "健康",
      hero: "主役の区画",
      navigation: "ナビゲーション",
      portfolio: "ポートフォリオ",
      pro: "プロ",
      productivity: "生産性",
      saas: "サブスク型サービス",
      support: "サポート",
    },
    features: {
      navigation: "ナビゲーション",
      "hero section": "主役の区画",
      charts: "グラフ",
      "data tables": "データ表",
      forms: "フォーム",
      footer: "フッター",
    },
    describe: ({ pageCount, blockCount, domainLabel, featureList }) =>
      `ブロック ${blockCount} 個で構成された ${pageCount} ページの${domainLabel}テンプレート · ${featureList}`,
    featureSeparator: "、",
  },
};

export const ZH_CN_STARTERS_COPY: StartersCopy = {
  pageDescription:
    "从 cogo 可直接使用的应用、文档或报告开始，然后在评审和分享之前，于聊天中打磨结果。",
  starterPaths: "起步路径",
  starterPathsLead:
    "先选择成果的类型。之后 cogo 就能围绕它准备来源、结果、评审循环和分享路径。",
  paths: {
    app: {
      title: "应用",
      description: "可交互的流程、界面，以及能够发布的产品体验。",
      cta: "开始做应用",
    },
    document: {
      title: "文档",
      description: "把政策、指南、提案、演示稿或源文件，变成活的文档。",
      cta: "添加文档来源",
    },
    report: {
      title: "报告",
      description: "基于来源的分析，包含表格、图表、摘要和评审准备度。",
      cta: "添加报告数据",
    },
    revision: {
      title: "修订",
      description: "打开已保存的工作，提出修改，并在分享前查看给出的结果。",
      cta: "打开已保存的工作",
    },
  },
  gallery: {
    searchPlaceholder: "搜索模板...",
    findSimilarBlocks: "查找相似区块",
    all: "全部",
    tier: (tierGrade) => `${tierGrade} 级`,
    blockCatalog: "区块目录",
    communityTemplates: "社区模板",
    comingSoon: "即将推出",
    emptyTitle: "没有找到模板",
    emptyBody:
      "没有与你的搜索或筛选相符的模板。可以清除一个筛选条件，或放宽搜索范围。",
    preview: "预览",
    create: "创建",
    previewAria: (labelName) => `预览 ${labelName}`,
    createFromAria: (labelName) => `从 ${labelName} 创建应用`,
    pages: (howMany) => `${howMany} 个页面`,
    blocks: (howMany) => `${howMany} 个区块`,
  },
  failedToStart: "无法从起步模板开始",
  newAppFallbackName: "新应用",
  catalog: {
    domains: {
      admin: "管理",
      agency: "代理机构",
      analytics: "分析",
      content: "内容",
      crm: "客户管理",
      ecommerce: "电商",
      food: "餐饮",
      health: "健康",
      portfolio: "作品集",
      productivity: "生产力",
      saas: "订阅式服务",
      support: "客户支持",
    },
    tags: {
      admin: "管理",
      agency: "代理机构",
      analytics: "分析",
      cards: "卡片",
      content: "内容",
      crm: "客户管理",
      data: "数据",
      ecommerce: "电商",
      food: "餐饮",
      footer: "页脚",
      forms: "表单",
      health: "健康",
      hero: "主视觉",
      navigation: "导航",
      portfolio: "作品集",
      pro: "专业",
      productivity: "生产力",
      saas: "订阅式服务",
      support: "客户支持",
    },
    features: {
      navigation: "导航",
      "hero section": "主视觉区块",
      charts: "图表",
      "data tables": "数据表格",
      forms: "表单",
      footer: "页脚",
    },
    describe: ({ pageCount, blockCount, domainLabel, featureList }) =>
      `由 ${blockCount} 个区块组成的 ${pageCount} 页${domainLabel}模板 · ${featureList}`,
    featureSeparator: "、",
  },
};

export const ZH_TW_STARTERS_COPY: StartersCopy = {
  pageDescription:
    "從 cogo 可直接使用的應用、文件或報告開始，接著在審查與分享之前，於聊天中打磨結果。",
  starterPaths: "起始路徑",
  starterPathsLead:
    "先選擇成果的類型。之後 cogo 就能圍繞它準備來源、結果、審查循環和分享路徑。",
  paths: {
    app: {
      title: "應用",
      description: "可互動的流程、畫面，以及能夠發布的產品體驗。",
      cta: "開始做應用",
    },
    document: {
      title: "文件",
      description: "把政策、指南、提案、簡報或原始檔案，變成活的文件。",
      cta: "新增文件來源",
    },
    report: {
      title: "報告",
      description: "以來源為依據的分析，包含表格、圖表、摘要與審查準備度。",
      cta: "新增報告資料",
    },
    revision: {
      title: "修訂",
      description: "開啟已儲存的工作，提出修改，並在分享前檢視提出的結果。",
      cta: "開啟已儲存的工作",
    },
  },
  gallery: {
    searchPlaceholder: "搜尋範本...",
    findSimilarBlocks: "尋找相似區塊",
    all: "全部",
    tier: (tierGrade) => `${tierGrade} 級`,
    blockCatalog: "區塊目錄",
    communityTemplates: "社群範本",
    comingSoon: "即將推出",
    emptyTitle: "找不到範本",
    emptyBody:
      "沒有與你的搜尋或篩選相符的範本。可以清除一個篩選條件，或放寬搜尋範圍。",
    preview: "預覽",
    create: "建立",
    previewAria: (labelName) => `預覽 ${labelName}`,
    createFromAria: (labelName) => `從 ${labelName} 建立應用`,
    pages: (howMany) => `${howMany} 個頁面`,
    blocks: (howMany) => `${howMany} 個區塊`,
  },
  failedToStart: "無法從起始範本開始",
  newAppFallbackName: "新應用",
  catalog: {
    domains: {
      admin: "管理",
      agency: "代理商",
      analytics: "分析",
      content: "內容",
      crm: "客戶管理",
      ecommerce: "電商",
      food: "餐飲",
      health: "健康",
      portfolio: "作品集",
      productivity: "生產力",
      saas: "訂閱式服務",
      support: "客戶支援",
    },
    tags: {
      admin: "管理",
      agency: "代理商",
      analytics: "分析",
      cards: "卡片",
      content: "內容",
      crm: "客戶管理",
      data: "資料",
      ecommerce: "電商",
      food: "餐飲",
      footer: "頁尾",
      forms: "表單",
      health: "健康",
      hero: "主視覺",
      navigation: "導覽",
      portfolio: "作品集",
      pro: "專業",
      productivity: "生產力",
      saas: "訂閱式服務",
      support: "客戶支援",
    },
    features: {
      navigation: "導覽",
      "hero section": "主視覺區塊",
      charts: "圖表",
      "data tables": "資料表格",
      forms: "表單",
      footer: "頁尾",
    },
    describe: ({ pageCount, blockCount, domainLabel, featureList }) =>
      `由 ${blockCount} 個區塊組成的 ${pageCount} 頁${domainLabel}範本 · ${featureList}`,
    featureSeparator: "、",
  },
};
